import { Text } from "@clipboard-health/ui-react";
import { Box, Stack } from "@mui/material";
import { useMonthRange } from "@src/appV2/lib/Calendar/useMonthRange";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { format, isBefore, parseISO, startOfDay } from "date-fns";
import { type ReactElement } from "react";
import { useController, type UseControllerProps } from "react-hook-form";

import { AvailabilityCalendar } from "../../../lib/AvailabilityCalendar";
import { AVAILABILITY_DATE_FORMAT } from "../../constants";
import { type WorkerAvailabilityCriteriaValues } from "../../types";

type FieldValue = Pick<WorkerAvailabilityCriteriaValues, "selectedAvailabilityDates">;

type FieldName = "selectedAvailabilityDates";

export interface WorkerAvailabilityCalendarProps extends UseControllerProps<FieldValue, FieldName> {
  isLoading: boolean;
  alreadyBookedDates: Date[];
  calendarOptions: {
    minDate: Date;
    maxDate: Date;
  };
}

export function WorkerAvailabilityCalendar(props: WorkerAvailabilityCalendarProps): ReactElement {
  const { name, alreadyBookedDates, calendarOptions, control, isLoading } = props;
  const {
    field,
    formState: { isSubmitting },
  } = useController<FieldValue, FieldName>({
    name,
    control,
  });

  const { showInfoToast } = useToast();

  const currentDate = new Date();

  const { dateRange, onPrevious, onNext } = useMonthRange({
    startDate: currentDate,
  });

  return (
    <Stack spacing={1}>
      <Box>
        <Text bold variant="h3">
          Availability
        </Text>
        <Text>Which dates are you available to work?</Text>
      </Box>

      <AvailabilityCalendar
        isLoading={isLoading || isSubmitting}
        dateRange={dateRange}
        options={{
          ...calendarOptions,
          headerTitle: format(dateRange.startDate, "MMMM yyyy"),
        }}
        availableDates={field.value.map((date) => parseISO(date))}
        blockedDates={alreadyBookedDates}
        onDateSelect={(params) => {
          const { value, isSelected, isDisabled } = params;

          const isPastDate = isBefore(value, startOfDay(new Date()));
          if (isPastDate) {
            return;
          }

          const newDate = format(value, AVAILABILITY_DATE_FORMAT);

          if (isDisabled) {
            showInfoToast(`You are working on ${format(value, "PPP")}.`);
            return;
          }

          if (isSelected) {
            field.onChange(field.value.filter((date) => date !== newDate));
          } else {
            field.onChange([...field.value, newDate]);
          }
        }}
        onPrevious={onPrevious}
        onNext={onNext}
      />
    </Stack>
  );
}
